import { useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";

import { ModelEventEnum } from "@/consts/AnalyticsEvent";
import { IconButton } from "@/features/common/components/IconButton";
import { useConfirmDialogContext } from "@/features/common/contexts/useConfirmDialogContext";
import { useAnalytics } from "@/features/common/hooks/useAnalytics";
import { useErrorHandling } from "@/features/common/hooks/useErrorHandling";
import { useInvalidateQueries } from "@/features/common/hooks/useInvalidateQueries";
import { EditModelAlbumFormInputs } from "@/features/modelPhoto/contexts/useEditModelAlbumFormContext";
import {
  AccessTypeEnum,
  useDeleteModelPhotosMutation,
} from "@/generated/graphql-hooks";
import DeletePhotoIcon from "@/icons/editAlbum/delete_photo.svg";
import DeletePhotoFilledIcon from "@/icons/editAlbum/delete_photo_filled.svg";

export const DeletePhotoButton = ({
  accessType,
}: {
  accessType: AccessTypeEnum;
}) => {
  const { trackEvent } = useAnalytics();
  const { handleGraphqlError } = useErrorHandling();
  const { mutateAsync: deleteModelPhotos } = useDeleteModelPhotosMutation();
  const { invalidateQueries } = useInvalidateQueries();
  const { reset } = useFormContext<EditModelAlbumFormInputs>();
  const modelPhotoIds = useWatch<
    Pick<EditModelAlbumFormInputs, "modelPhotoIds">
  >({ name: "modelPhotoIds" });
  const { openConfirmDialog } = useConfirmDialogContext();

  const onDeletePhoto = async () => {
    if (!modelPhotoIds?.length) return;
    try {
      await deleteModelPhotos({ modelPhotoIds });
      invalidateQueries(["ModelPhotos.infinite"]);
      reset({ modelPhotoIds: [] });
      toast.success("成功刪除照片");
      trackEvent(ModelEventEnum.DELETE_PHOTOS, { accessType });
    } catch (error) {
      handleGraphqlError(error, {
        tags: {
          action: "EditPhotoButtonGroup.onDeletePhoto",
        },
      });
    }
  };

  return (
    <IconButton
      onClick={() => {
        openConfirmDialog({
          title: "刪除照片",
          content: "刪除的照片無法復原，請問是否確定刪除？",
          onConfirm: onDeletePhoto,
        });
      }}
      disabled={!modelPhotoIds?.length}
    >
      {!modelPhotoIds?.length ? <DeletePhotoIcon /> : <DeletePhotoFilledIcon />}
    </IconButton>
  );
};
